import iconVideoUrl from "@components/Image/icon-video.svg?url";
import { useAppSelector } from "@hooks/redux";
import report from "@report";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import Player, { Events, IPlayerOptions } from "xgplayer";
import "xgplayer/dist/index.min.css";
import "./player.scss";

const VIDEO_START_ICON_SIZE = 64;

/** xgplayer icons 函数必须返回 DOM Element，不能返回 HTML 字符串 */
const createVideoStartIcon = () => {
  const img = document.createElement("img");
  img.src = iconVideoUrl;
  img.width = VIDEO_START_ICON_SIZE;
  img.height = VIDEO_START_ICON_SIZE;
  img.alt = "";
  return img;
};

const EventNames = [
  Events.LOAD_START,
  Events.LOADED_DATA,
  Events.PLAY,
  Events.PAUSE,
  Events.ENDED,
  Events.ERROR,
  Events.PLAYING,
  Events.SEEKING,
  Events.SEEKED,
  Events.WAITING,
  Events.CANPLAY,
  Events.CANPLAY_THROUGH,
  Events.READY,
  Events.AUTOPLAY_PREVENTED,
  Events.AUTOPLAY_STARTED,
  Events.COMPLETE,
  Events.URL_CHANGE,
  Events.DESTROY,
  Events.REPLAY,
  Events.RETRY,
];

const defaultOption = {
  autoplay: true,
  loop: true,
  volume: 0.8,
  playsinline: true,
  // isShowPause: true → 暂停时持续显示中央播放按钮；播放中由 start 插件自动隐藏
  start: { isShowPause: true },
  icons: {
    startPlay: createVideoStartIcon,
    startPause: createVideoStartIcon,
  },
  fluid: true,
  ignores: ["cssFullScreen", "playbackrate"],
};

interface EventHandleItem {
  eventName: string;
  handle: (data?: any) => void;
}

export const useVideoPlayerReport = (kalo_pos: string, kalo_type: string) => {
  const refEvents = useRef<
    {
      eventName: string;
      eventTime: number;
      eventMsg: string;
      videoTime: number | undefined;
      duration: number | undefined;
      videoUuid: string;
    }[]
  >([]);

  const reportVideoEvents = (videoEvents: any) => {
    report.event("kalo_player", { kalo_pos, kalo_type, kalo_value: videoEvents });
  };

  useEffect(() => {
    const report = () => {
      if (refEvents.current) {
        if (refEvents.current.length > 0) {
          reportVideoEvents(refEvents.current);
        }
        refEvents.current = [];
      }
    };
    const timerID = setInterval(() => {
      report();
    }, 5000);

    return () => {
      clearTimeout(timerID);
      report();
    };
  }, []);

  return { refEvents, reportVideoEvents };
};

export const useVideoPlayer = (
  options: IPlayerOptions,
  eventHandles: EventHandleItem[],
  { kalo_pos, kalo_type }: { kalo_pos: string; kalo_type: string },
) => {
  const refPlayer = useRef<Player | null>(null);
  const { refEvents, reportVideoEvents } = useVideoPlayerReport(kalo_pos, kalo_type);
  const { language } = useAppSelector((state) => state.globalReducer);
  const [autoplayMuted, setAutoplayMuted] = useState(false);

  useEffect(() => {
    let player: Player;
    if (options.url) {
      player = new Player({ lang: language, autoplayMuted, ...defaultOption, ...options });
      const videoUuid = uuidv4();
      const handlePlayerEvent = (eventName: string, data: any) => {
        let eventMsg = "";
        if (eventName === "error") {
          eventMsg = data?.message || "";
        }
        refEvents.current.push({
          eventName,
          eventTime: new Date().getTime(),
          eventMsg,
          videoTime: refPlayer.current?.currentTime,
          duration: refPlayer.current?.duration,
          videoUuid,
        });

        if (eventName === "destroy") {
          if (refEvents.current.length > 0) {
            reportVideoEvents(refEvents.current);
          }
          refEvents.current = [];
        }

        if (eventName === Events.AUTOPLAY_PREVENTED) {
          setAutoplayMuted(true);
        }
      };

      for (let eventName of EventNames) {
        player.on(eventName, (data) => {
          handlePlayerEvent(eventName, data);
        });
      }

      // 外部传入事件回调
      for (let eventHandle of eventHandles) {
        player.on(eventHandle.eventName, eventHandle.handle);
      }

      refPlayer.current = player;
    }

    return () => {
      if (player) {
        player.destroy();
      }
    };
  }, [autoplayMuted, options.url]);

  return { refPlayer };
};
